/**
 * Öffentliche Terminanfrage.
 *
 * Ohne Anmeldung erreichbar, deshalb dreifach geschützt: Turnstile, ein für
 * Menschen unsichtbares Feld und die Zeit zwischen Aufruf und Absenden. Die
 * beiden letzten sind hier zu sehen; die Prüfung findet auf dem Server statt.
 *
 * Der Ablauf ist bewusst dreistufig: Anfrage, Bestätigung per Link, dann die
 * Zusage durch das Prüfbüro. Nichts trägt sich von selbst ein.
 *
 * Diese Seite ist die einzige, die jemand sieht, der SPÜR noch nicht kennt –
 * anders als der Rest der Anwendung, den nur das Prüfbüro benutzt. Sie ist
 * deshalb bewusst als Seite gestaltet und nicht als Formularmaske: dunkler
 * Kopf mit dem, was das Büro ausmacht, darunter das Formular, darunter die
 * Stimmen der Auftraggeber.
 *
 * Alle Bewegung darauf ist Zugabe. Sie entfällt vollständig, wenn jemand
 * „Bewegung reduzieren" eingestellt hat – siehe `Bewegung.ts`.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { api } from '../api/klient'
import { PRUEFARTTEXT, type Pruefart } from '../api/typen'
import { Fehlerhinweis } from '../bausteine/Hinweise'
import logo from '../assets/spuer-logo.png'
import { useEinblenden } from './Bewegung'
import { Referenzkarussell, type Referenz } from './Referenzkarussell'

type Angaben = {
  mandant_name: string
  briefkopf: string | null
  zertifikat: string | null
  turnstile_schluessel: string | null
  referenzen: Referenz[]
}

const ARTEN: Pruefart[] = ['hauptuntersuchung', 'operativ', 'abnahme', 'nachpruefung']

declare global {
  interface Window {
    turnstile?: {
      render: (ziel: HTMLElement, optionen: Record<string, unknown>) => string
    }
  }
}

export function Terminbuchung() {
  const [angaben, setzeAngaben] = useState<Angaben | null>(null)
  const [gesendet, setzeGesendet] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)

  // Zeitpunkt des Seitenaufrufs. Der Server weist ab, wer in unter drei
  // Sekunden ausfüllt – das schafft kein Mensch.
  const geladenUm = useRef(Date.now() / 1000)
  const turnstileZiel = useRef<HTMLDivElement>(null)
  const [turnstileMarke, setzeTurnstileMarke] = useState<string | null>(null)

  useEffect(() => {
    void api
      .holen<Angaben>('/api/oeffentlich/angaben')
      .then(setzeAngaben)
      .catch(setzeFehler)
  }, [])

  // Turnstile nur laden, wenn ein Schlüssel hinterlegt ist.
  useEffect(() => {
    const schluessel = angaben?.turnstile_schluessel
    if (!schluessel || !turnstileZiel.current) return

    const skript = document.createElement('script')
    skript.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    skript.async = true
    skript.onload = () => {
      window.turnstile?.render(turnstileZiel.current!, {
        sitekey: schluessel,
        language: 'de',
        callback: (marke: string) => setzeTurnstileMarke(marke),
      })
    }
    document.head.appendChild(skript)
    return () => skript.remove()
  }, [angaben?.turnstile_schluessel])

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)

    const formular = new FormData(ereignis.currentTarget)
    const roh = Object.fromEntries(
      [...formular.entries()].filter(([, w]) => String(w).trim() !== ''),
    )

    try {
      const antwort = await api.senden<{ meldung: string }>(
        '/api/oeffentlich/terminanfrage',
        {
          ...roh,
          anzahl_geraete: roh.anzahl_geraete ? Number(roh.anzahl_geraete) : null,
          geladen_um: geladenUm.current,
          turnstile_marke: turnstileMarke,
        },
      )
      setzeGesendet(antwort.meldung)
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  if (gesendet) {
    return (
      <Rahmen angaben={angaben}>
        <div className="oe-karte oe-erfolg">
          <Haken />
          <h2 className="oe-karte-titel text-stufe-ob">Anfrage abgeschickt</h2>
          <p className="mt-2">{gesendet}</p>
          <p className="mt-3 text-base text-slate-700">
            Kommt keine E-Mail an, sehen Sie bitte im Spam-Ordner nach. Der Link ist
            sieben Tage gültig.
          </p>
        </div>
      </Rahmen>
    )
  }

  return (
    <Rahmen angaben={angaben}>
      <form onSubmit={absenden} className="space-y-4">
        <Abschnitt titel="Ihre Einrichtung">
          <div>
            <label htmlFor="einrichtung">Gemeinde, Kita oder Schule *</label>
            <input id="einrichtung" name="einrichtung" required minLength={2} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="ansprechpartner">Ansprechpartner *</label>
              <input id="ansprechpartner" name="ansprechpartner" required minLength={2} />
            </div>
            <div>
              <label htmlFor="email">E-Mail *</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoCapitalize="none"
                spellCheck={false}
              />
            </div>
            <div>
              <label htmlFor="telefon">Telefon</label>
              <input id="telefon" name="telefon" type="tel" />
            </div>
            <div>
              <label htmlFor="strasse">Straße</label>
              <input id="strasse" name="strasse" />
            </div>
            <div className="grid grid-cols-[7rem_1fr] gap-3">
              <div>
                <label htmlFor="plz">PLZ</label>
                <input id="plz" name="plz" inputMode="numeric" />
              </div>
              <div>
                <label htmlFor="ort">Ort</label>
                <input id="ort" name="ort" />
              </div>
            </div>
          </div>
        </Abschnitt>

        <Abschnitt titel="Was geprüft werden soll">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="pruefart">Art der Prüfung</label>
              <select id="pruefart" name="pruefart" defaultValue="hauptuntersuchung">
                {ARTEN.map((a) => (
                  <option key={a} value={a}>
                    {PRUEFARTTEXT[a]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="anzahl_geraete">Anzahl Spielgeräte, geschätzt</label>
              <input id="anzahl_geraete" name="anzahl_geraete" inputMode="numeric" />
            </div>
            <div>
              <label htmlFor="wunschdatum">Wunschtermin</label>
              <input id="wunschdatum" name="wunschdatum" type="date" />
            </div>
            <div>
              <label htmlFor="zeitfenster">Zeitfenster</label>
              <select id="zeitfenster" name="zeitfenster" defaultValue="">
                <option value="">— egal —</option>
                <option value="vormittag">vormittags</option>
                <option value="nachmittag">nachmittags</option>
                <option value="ganztags">ganztägig</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="nachricht">Anmerkungen</label>
            <textarea
              id="nachricht"
              name="nachricht"
              placeholder="Zum Beispiel: bitte möglichst in den Herbstferien."
            />
          </div>
        </Abschnitt>

        {/*
          Honigtopf. Für Menschen unsichtbar – aber nicht per display:none,
          weil manche Automaten das erkennen. Stattdessen aus dem Bild
          geschoben und für die Sprachausgabe ausgeblendet.
        */}
        <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
          <label htmlFor="webseite">Webseite</label>
          <input id="webseite" name="webseite" tabIndex={-1} autoComplete="off" />
        </div>

        {angaben?.turnstile_schluessel && <div ref={turnstileZiel} className="min-h-[70px]" />}

        <Fehlerhinweis fehler={fehler} />

        <button type="submit" className="oe-absenden" disabled={laeuft}>
          {laeuft ? 'Sendet …' : 'Anfrage abschicken'}
        </button>

        <p className="text-base text-slate-600">
          Nach dem Abschicken erhalten Sie eine E-Mail mit einem Bestätigungslink. Erst
          nach dem Klick geht die Anfrage bei uns ein – so kann niemand mit einer fremden
          Adresse Termine vereinbaren.
        </p>
      </form>
    </Rahmen>
  )
}

/** Bestätigungsseite: löst den Link aus der E-Mail ein. */
export function TerminBestaetigen() {
  const [zustand, setzeZustand] = useState<'laeuft' | 'fertig' | 'fehler'>('laeuft')
  const [meldung, setzeMeldung] = useState('')

  // Das Token ist nur einmal einlösbar. Ohne diese Sperre schickt React im
  // StrictMode zweimal, und der zweite Aufruf zeigt „bereits benutzt" – obwohl
  // der erste durchging. Ein Doppelklick auf den Link in der E-Mail hätte
  // dieselbe Wirkung.
  const schonGesendet = useRef(false)

  useEffect(() => {
    if (schonGesendet.current) return
    schonGesendet.current = true

    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) {
      setzeZustand('fehler')
      setzeMeldung('In der Adresse fehlt der Bestätigungscode.')
      return
    }
    api
      .senden<{ meldung: string }>('/api/oeffentlich/terminanfrage/bestaetigen', { token })
      .then((a) => {
        setzeMeldung(a.meldung)
        setzeZustand('fertig')
      })
      .catch((f) => {
        setzeMeldung(
          f instanceof Error ? f.message : 'Der Link ist ungültig oder abgelaufen.',
        )
        setzeZustand('fehler')
      })
  }, [])

  return (
    <Rahmen
      angaben={null}
      schlagzeile="Terminanfrage bestätigen"
      vorspann="Noch ein Schritt: der Klick auf den Link aus der E-Mail schließt die Anfrage ab."
    >
      <div
        className={`oe-karte ${
          zustand === 'fertig'
            ? 'border-stufe-ob bg-stufe-ob-grund'
            : zustand === 'fehler'
              ? 'border-stufe-3 bg-stufe-3-grund'
              : ''
        }`}
      >
        <h2 className="text-xl font-bold">
          {zustand === 'laeuft'
            ? 'Prüft den Link …'
            : zustand === 'fertig'
              ? 'Bestätigt'
              : 'Das hat nicht geklappt'}
        </h2>
        {meldung && <p className="mt-2">{meldung}</p>}
        {zustand === 'fehler' && (
          <a href="/termin" className="knopf-zweit mt-3">
            Neue Anfrage stellen
          </a>
        )}
      </div>
    </Rahmen>
  )
}

function Rahmen({
  angaben,
  schlagzeile = 'Prüftermin anfragen',
  vorspann = 'Hauptuntersuchung, operative Inspektion, Abnahme und Nachprüfung von Spielplätzen – geprüft vor Ort, dokumentiert im Bericht.',
  children,
}: {
  angaben: Angaben | null
  /* Die Bestätigungsseite ist keine Anfrageseite. Vorher stand über beiden
     dieselbe Überschrift; im kleinen Kopf von früher fiel das kaum auf, in
     einem großen wäre es falsch. */
  schlagzeile?: string
  vorspann?: string
  children: React.ReactNode
}) {
  const stimmen = angaben?.referenzen ?? []

  return (
    <div className="oe-seite">
      <Kopf angaben={angaben} schlagzeile={schlagzeile} vorspann={vorspann} />

      {/* Zieht die Karte in den dunklen Kopf hinein. Das ist der einzige
          Grund für den negativen Rand: der Übergang soll nicht wie eine
          Naht zwischen zwei Seiten aussehen. */}
      <main className="oe-inhalt">{children}</main>

      {stimmen.length > 0 && <Referenzkarussell referenzen={stimmen} />}

      <Fuss angaben={angaben} />
    </div>
  )
}

function Kopf({
  angaben,
  schlagzeile,
  vorspann,
}: {
  angaben: Angaben | null
  schlagzeile: string
  vorspann: string
}) {
  return (
    <header className="oe-kopf">
      {/* Zwei sehr weiche Lichter, die langsam wandern. Rein dekorativ und
          deshalb aus dem Vorlesebaum genommen. Sie liegen hinter allem und
          nehmen keine Klicks an. */}
      <div className="oe-kopf-licht" aria-hidden="true" />

      <div className="oe-kopf-inhalt">
        <img
          src={logo}
          alt={angaben?.mandant_name ?? 'SPÜR'}
          className="oe-logo oe-anflug"
          style={{ animationDelay: '0ms' }}
        />

        <h1 className="oe-schlagzeile oe-anflug" style={{ animationDelay: '80ms' }}>
          {schlagzeile}
        </h1>

        <p className="oe-vorspann oe-anflug" style={{ animationDelay: '160ms' }}>
          {vorspann}
        </p>

        <ul className="oe-merkmale oe-anflug" style={{ animationDelay: '240ms' }}>
          <li>DIN EN 1176/1177</li>
          <li>DIN 18034</li>
          <li>DGUV 202-022</li>
          {angaben?.zertifikat && <li>Zertifikat {angaben.zertifikat}</li>}
          <li>Spielplatzprüfer nach DIN 79161-2</li>
        </ul>
      </div>

      {/* Schräge Kante zum weißen Bereich. Als SVG statt als gedrehtes
          Kästchen, weil das bei jeder Breite sauber aufgeht. */}
      <svg className="oe-kante" viewBox="0 0 1440 48" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 48 L1440 0 L1440 48 Z" fill="currentColor" />
      </svg>
    </header>
  )
}

function Fuss({ angaben }: { angaben: Angaben | null }) {
  return (
    <footer className="oe-fuss">
      {angaben?.briefkopf && <p className="oe-fuss-anschrift">{angaben.briefkopf}</p>}
      {angaben?.zertifikat && (
        <p className="oe-fuss-zeile">
          Zertifikat {angaben.zertifikat} · Spielplatzprüfer nach DIN 79161-2
        </p>
      )}
    </footer>
  )
}

/**
 * Ein Abschnitt, der einblendet, sobald er ins Bild kommt.
 *
 * Eigene Komponente und nicht der Hook an jeder Stelle: so steht an jedem
 * Abschnitt nur, dass er einblendet, und nicht wie.
 */
function Abschnitt({
  titel,
  children,
}: {
  titel: string
  children: React.ReactNode
}) {
  const verweis = useEinblenden<HTMLElement>()
  return (
    <section ref={verweis} className="oe-karte aufblende">
      <h2 className="oe-karte-titel">{titel}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  )
}


/** Der Haken auf der Bestätigung. Zeichnet sich einmal selbst. */
function Haken() {
  return (
    <svg
      className="oe-haken"
      viewBox="0 0 52 52"
      width="52"
      height="52"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle className="oe-haken-kreis" cx="26" cy="26" r="23" />
      <path className="oe-haken-strich" d="M15 27l8 8 15-16" />
    </svg>
  )
}
